import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Tilt } from 'react-tilt';
import { 
  Megaphone, 
  Users, 
  Target, 
  Smartphone, 
  Briefcase, 
  Palette, 
  Globe, 
  ShoppingCart, 
  Mail, 
  Search,
  CreditCard,
  Server,
  Bot,
  Share2,
  Glasses,
  FolderKanban,
  ArrowLeft,
  Calendar,
  MessageCircle
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const WHATSAPP_URL = 'https://wa.me/+523224750476';

const ALL_SERVICES = [
  { icon: Megaphone, key: 'marketing', color: 'var(--primary-main)' },
  { icon: Users, key: 'socialMedia', color: 'var(--accent-gold)' },
  { icon: Target, key: 'ads', color: 'var(--primary-light)' },
  { icon: Smartphone, key: 'mobileApps', color: 'var(--accent-yellow)' },
  { icon: Briefcase, key: 'consulting', color: 'var(--primary-main)' },
  { icon: Palette, key: 'branding', color: 'var(--accent-gold)' },
  { icon: Globe, key: 'webDesign', color: 'var(--primary-light)' },
  { icon: ShoppingCart, key: 'ecommerce', color: 'var(--accent-yellow)' },
  { icon: Mail, key: 'emailMarketing', color: 'var(--primary-main)' },
  { icon: Search, key: 'seo', color: 'var(--accent-gold)' },
  { icon: CreditCard, key: 'payments', color: 'var(--primary-light)' },
  { icon: Server, key: 'hosting', color: 'var(--accent-yellow)' },
  { icon: Bot, key: 'ai', color: 'var(--primary-main)' },
  { icon: Share2, key: 'socialMediaMgmt', color: 'var(--accent-gold)' },
  { icon: Glasses, key: 'ar', color: 'var(--primary-light)' },
  { icon: FolderKanban, key: 'projectMgmt', color: 'var(--accent-yellow)' },
];

const ServiceDetailCard = ({ icon: Icon, serviceKey, color, index, t }) => {
  const service = t(`servicesPage.categories.${serviceKey}`);
  const name = typeof service === 'object' ? service.name : service;
  const description = typeof service === 'object' ? service.description : '';
  const price = typeof service === 'object' ? service.price : '';

  return (
    <Tilt
      options={{
        max: 12,
        scale: 1.02,
        speed: 300,
        glare: true,
        'max-glare': 0.15,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
        viewport={{ once: true }}
        data-testid={`service-detail-card-${index}`}
        className="group h-full p-6 rounded-2xl glass border border-[var(--border-subtle)] hover:border-[var(--border-highlight)] transition-all duration-500 flex flex-col"
        style={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
        }}
      >
        <div className="flex items-center gap-4 mb-4">
          {/* Icon */}
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 flex-shrink-0"
            style={{ backgroundColor: `${color}20` }}
          >
            <Icon className="w-6 h-6 transition-colors duration-300" style={{ color }} />
          </div>
          
          {/* Title & Price */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-bold text-[var(--text-primary)] group-hover:text-[var(--accent-gold)] transition-colors duration-300">
              {name}
            </h3>
            {price && (
              <p className="text-sm text-[var(--accent-gold)] font-semibold">
                {t('servicesPage.startingFrom')} {price}
              </p>
            )}
          </div>
        </div>
        
        {/* Description */}
        {description && (
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4 flex-1">
            {description}
          </p>
        )}
        
        {/* WhatsApp CTA */}
        <a
          href={`${WHATSAPP_URL}?text=${encodeURIComponent(`Hi! I'm interested in your ${name} service.`)}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`service-whatsapp-${serviceKey}`}
          className="mt-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[var(--primary-main)] bg-opacity-20 text-[var(--text-primary)] text-sm font-medium hover:bg-opacity-40 transition-all duration-300 group-hover:bg-[var(--accent-gold)] group-hover:bg-opacity-20"
        >
          <MessageCircle className="w-4 h-4" />
          {t('servicesPage.contactUs')}
        </a>
        
        {/* Hover accent */}
        <div className="mt-4 h-0.5 w-0 group-hover:w-full transition-all duration-500 rounded-full" style={{ backgroundColor: color }} />
      </motion.div>
    </Tilt>
  );
};

export default function ServicesPage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen pt-24" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[var(--primary-main)] rounded-full blur-[200px] opacity-15" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[var(--accent-gold)] rounded-full blur-[150px] opacity-10" />
        
        <div className="container-custom relative z-10">
          {/* Back link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link 
              to="/" 
              data-testid="back-to-home"
              className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary-light)] transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t('servicesPage.backToHome')}</span>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="gradient-text-gold">{t('servicesPage.headline')}</span>
            </h1>
            <p className="text-base md:text-lg text-[var(--text-secondary)] mb-8">
              {t('servicesPage.subheadline')}
            </p>
            <a
              href="https://calendly.com/dmt-a-master"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-consultation-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              {t('nav.bookCall')}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24" data-testid="services-grid-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {ALL_SERVICES.map((service, idx) => (
              <ServiceDetailCard
                key={service.key}
                icon={service.icon}
                serviceKey={service.key}
                color={service.color}
                index={idx}
                t={t}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 relative" data-testid="services-cta-section">
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--primary-dark)] to-transparent opacity-10" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="glass-strong rounded-3xl p-8 md:p-12 lg:p-16 text-center border border-[var(--border-highlight)]"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-[var(--text-primary)]">
              {t('servicesPage.readyToTransform')}
            </h2>
            <p className="text-base md:text-lg text-[var(--text-secondary)] max-w-2xl mx-auto mb-8">
              {t('servicesPage.readyToTransformDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://calendly.com/dmt-a-master"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-book-call-btn"
                className="btn-primary"
              >
                {t('servicesPage.bookConsultation')}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-whatsapp-btn"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                {t('servicesPage.chatWhatsApp')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
